// HTTP-клиент публичного API виджета (config/routes/widget.rb).
// Посетителя идентифицирует visitor_key из localStorage — сессий и кук нет.
import { pageLanguage } from './language.ts';
import { ThemePreference } from './theme';

export interface AssistantCard {
  name: string;
  role: string;
  greeting: string;
  avatar_url: string | null;
  /** Короткий MP4-луп аватара — приоритетнее avatar_url в шапке. */
  avatar_video_url: string | null;
}

export type WidgetTexts = Record<string, string>;

/** Кнопка/чип в блоке: `url` открывает ссылку в новой вкладке (найм, витрина,
 *  тарифы), `send` отправляет текст как новое сообщение (снова ловит сценарий). */
export interface ChatAction {
  label: string;
  url?: string;
  send?: string;
  style?: 'primary' | 'secondary';
}

/** Карточка витрины сотрудников (Showcase::Roster). */
export interface RosterCard {
  slug: string;
  name: string;
  role: string;
  description: string;
  avatar: string;
  /** Honesty-обводка §3 («собираю заявки, сложное передаю человеку»). */
  honesty?: string;
  hire: { label: string; url: string };
}

/** Фото-карточка работ владельца (CatalogEntity): миниатюра + заголовок + мета
 *  («120 м² · 250 мм · 12 дней · Гурьевск»). Бот показывает их по маркеру
 *  [[галерея]]; блок собирает сервер из галереи сотрудника. */
export interface GalleryCard {
  /** Абсолютный blob-URL ActiveStorage (виджет живёт на чужих origin). */
  image: string;
  title: string;
  meta?: string;
}

/** Карточка товара магазина (CatalogEntity kind: 'product'): фото, название,
 *  цена подписью и id позиции В МАГАЗИНЕ — по нему виджет кладёт товар в
 *  корзину сайта. Бот показывает их по маркеру [[товары:запрос]]. */
export interface ShopCard {
  image: string;
  title: string;
  /** Цена как её написал магазин («1630 ₽») — мы её не считаем и не округляем. */
  price?: string;
  /** Страница товара на сайте владельца. */
  url?: string;
  /** Идентификатор позиции в магазине («composition:13»). */
  id?: string;
}

/** Мост к корзине магазина (Assistants::ShopSettings): пути на САЙТЕ владельца
 *  и имена полей его формы. Виджет живёт на том же origin, поэтому кладёт товар
 *  сессией самого посетителя — наш сервер заказов не касается. */
export interface ShopConfig {
  /** Адрес корзины «своего сайта»; при адаптере движка может отсутствовать —
   *  адаптер знает его сам. */
  cart_url?: string;
  id_field?: string;
  type_field?: string;
  /** Поле количества: без него заказ «×3» из разговора положить нечем. */
  qty_field?: string;
  checkout_url?: string;
  /** Адаптер движка магазина (doc/shop_adapters.md): отдельный файл
   *  /widget-adapters/<name>.js, который виджет догружает только на сайте
   *  этого движка — общий бандл от него не растёт. */
  adapter?: string;
}

/** Позиция подтверждённого заказа для корзины сайта (Orders::Cart). */
export interface CartItem {
  /** Идентификатор товара в магазине владельца (external_id каталога). */
  id: string;
  quantity: number;
  title: string;
}

/** Подтверждённый заказ — в корзину САЙТА: кладёт виджет, оформляет человек. */
export interface CartBlock {
  items: CartItem[];
  shop?: ShopConfig;
}

/** Контакты, названные в разговоре: черновик заявки этого же посетителя. */
export interface ContactInfo {
  name?: string;
  phone?: string;
  email?: string;
}

/** QR на оплату (маркер [[оплата]]): картинка владельца плюс его подпись —
 *  кому платит гость и что после перевода напишет живой человек. Приходит той
 *  же формой, что галерея (items), поэтому старые бандлы её просто пропустят. */
export interface PaymentCard {
  /** Абсолютный blob-URL картинки QR. */
  image: string;
  /** Подпись владельца под кодом; заголовка у QR нет. */
  note?: string;
}

/** Коллега вошёл в разговор (§8, Conversations::Invite): дальше отвечает он —
 *  виджет меняет адресата на его public_key и шапку на его карточку. Текст
 *  служебной строки приходит с сервера (строки живут в локали, не в бандле). */
export interface SwitchBlock {
  type: 'switch';
  /** public_key коллеги — новый адресат ленты. */
  key: string;
  text: string;
}

/** Структурный блок под сообщением консультанта (Assistants::Scenario):
 *  витрина карточек, кнопки-действия, чипы быстрых ответов, фото работ,
 *  передача разговора коллеге. */
export type ChatBlock =
  | { type: 'roster'; items: RosterCard[] }
  | { type: 'gallery'; items: GalleryCard[] }
  | { type: 'shop'; items: ShopCard[]; shop?: ShopConfig }
  | { type: 'payment'; items: PaymentCard[] }
  /** page — адрес страницы-источника ответа (Assistants::PageLink): метка
   *  сервера для дедупа, виджет её не читает. */
  | { type: 'actions'; items: ChatAction[]; page?: string }
  | { type: 'quick'; items: ChatAction[] }
  | SwitchBlock;

/** Постоянный чип «Продолжить в Telegram/MAX»: kind канала + готовая подпись
 *  с сервера. Ссылки здесь нет — диплинк с одноразовым токеном запрашивается
 *  в момент клика (fetchContinueLink). */
export interface ContinueOption {
  kind: string;
  label: string;
}

/** Подпись платформы под полем ввода — только у бесплатного сотрудника. */
export interface WidgetBranding {
  text: string;
  url: string;
  logo_url: string | null;
}

export interface WidgetConfig {
  assistant: AssistantCard;
  texts: WidgetTexts;
  /** Подпись «Работает на ассИИстент»: null/нет поля — тариф платный (или
   *  сервер старый), подпись не рисуем. */
  branding?: WidgetBranding | null;
  /** Мост к корзине магазина — в конфиге, чтобы на странице оформления знать
   *  адаптер движка ДО первого ответа бота (заполнение формы заказа). */
  shop?: ShopConfig | null;
  /** Стартовые чипы под приветствием (только у консультанта; иначе пусто). */
  chips?: ChatAction[];
  /** Каналы «продолжить» — только подключённые у тенанта (иначе пусто). */
  continue?: ContinueOption[];
  /** Ссылка шапки «продолжить у того же бота» (аватар+имя кликабельны): приоритет
   *  MAX, иначе Telegram; null — подключённых ботов нет (шапка не ссылка). */
  header_bot_url?: string | null;
  /** Тумблер владельца «звук нового сообщения» (кабинет → Настройки). */
  sound?: boolean;
  /** Тумблер владельца «автооткрытие чата» (один раз на посетителя). */
  auto_open?: boolean;
  /** Пауза до автооткрытия в СЕКУНДАХ (настройка владельца). Поля нет у старого
   *  конфига — виджет берёт свой дефолт (autoOpenDelayMs). */
  auto_open_delay?: number;
  /** Демо холодной кампании: раскрывать чат при каждом заходе, а не один раз
   *  на посетителя — вся страница про этот разговор. */
  auto_open_always?: boolean;
  /** Настройка владельца «тема виджета»: 'auto' — по сайту (data-theme/фон),
   *  'light'/'dark' — жёстко (см. resolveWidgetTheme). */
  theme?: ThemePreference;
  /** Сторона, с которой висит пузырь: 'right' (по умолчанию) или 'left'. */
  side?: 'right' | 'left';
  /** Отступ пузыря от низа окна, пиксели. Было зашито 20, стало настройкой:
   *  у клиента виджет накрывал кнопку прокрутки, а поправить со своей стороны
   *  он не мог — правило живёт внутри shadow DOM. */
  offset_bottom?: number;
  /** Отступ пузыря от боковой стороны (той, что в `side`), пиксели. Нужен там,
   *  где у сайта своя плавающая кнопка: с разными отступами пузырь и кнопка
   *  стоят вкривь, а совпадение центров видно глазом (MKEL, 04.09.2026). */
  offset_side?: number;
  /** Селектор элемента сайта для привязки пузыря; >>> переходит в open Shadow DOM. */
  launcher_anchor?: string | null;
  /** Фразы приглашения у лаунчера (настройка сотрудника): несколько — тизер
   *  печатает их по кругу, одна — показывает статично, поля нет или пусто —
   *  прежнее приглашение из локали (см. teaser.ts). */
  teaser_phrases?: string[];
  /** Публичный VAPID-ключ Web Push (applicationServerKey подписки);
   *  null — пуш на платформе выключен, подписку не предлагаем. */
  push_key?: string | null;
  /** Куда открывать ссылки ленты: 'self' — в этом окне (консультант платформы
   *  на своём сайте: виджет там на каждой странице, история подхватится),
   *  'blank' или без поля — новая вкладка (сайт клиента). См. navigation.ts. */
  page_target?: 'self' | 'blank' | 'frame';
  /** Этот посетитель заблокирован (spec §12): виджета для него больше нет —
   *  ни панели, ни пузыря, ни таймеров. Остальных полей в таком ответе тоже
   *  нет: сервер молча отдаёт один флаг. Поле НЕОБЯЗАТЕЛЬНОЕ — старый сервер
   *  его не шлёт, и новый бандл ведёт себя как раньше. */
  blocked?: boolean;
}

/** Вложение реплики: подписанный blob-URL ActiveStorage (файлы хранятся на
 *  сервере платформы; в языковую модель не передаются — spec §9). */
export interface ChatFile {
  name: string;
  url: string;
  size: number;
  content_type: string;
}

export interface ChatMessage {
  id: number;
  role: 'visitor' | 'assistant' | 'operator' | 'system';
  content: string;
  /** Бот попросил открыть форму заявки (маркер [[заявка]]). */
  lead?: boolean;
  /** Контакты, уже названные в разговоре: ими предзаполняется форма заявки.
   *  Приходят только вместе с lead и только непустые (черновик этого же
   *  разговора, а не выдача ПД по запросу). */
  lead_prefill?: ContactInfo;
  /** Подтверждённый заказ — в корзину САЙТА (Orders::Cart): приходит только у
   *  ответа того хода, когда гость сказал «да». Виджет кладёт позиции в
   *  корзину магазина ОДИН раз на сообщение и рисует кнопку «Оформить заказ». */
  cart?: CartBlock;
  /** Ответ профильный (услуги компании применимы к запросу) — сервер ставит флаг
   *  только при topic=='profile' (spec §5). Цель Метрики site_chat_reply летит
   *  ТОЛЬКО тогда; оффтоп/абьюз/забытый маркер → поле отсутствует. Сам класс
   *  разговора браузеру не раскрывается — только этот булев (§2 PII). */
  on_topic?: boolean;
  /** Витринные блоки консультанта (карточки/кнопки/чипы). */
  blocks?: ChatBlock[];
  /** Подсказка бота для формы страницы (см. FormPatch): приходит только когда
   *  бот действительно предложил значения. */
  patch?: FormPatch;
  /** Части длинного ответа (Assistants::BubbleSplitter): виджет раскрывает их
   *  по одному с «печатает…» паузой; content остаётся полным текстом. */
  bubbles?: string[];
  /** Сколько ещё миллисекунд держать «печатает…» до показа ответа — остаток,
   *  посчитанный сервером в момент опроса (часы гостя не участвуют). Темп
   *  ответа считает сервер, но в воркере не спит. */
  reveal_in_ms?: number;
  /** Read-receipt (виджет-паритет, волна 2): реплика посетителя прочитана
   *  (ассистент ответил ИЛИ оператор открыл диалог) — виджет рисует ✓✓. Есть
   *  только у visitor-реплик и только когда true. */
  seen?: boolean;
  /** Вложения посетителя (файлы виджета) — только когда есть. */
  files?: ChatFile[];
  created_at: string;
}

export interface MessagesResponse {
  conversation_id?: number;
  messages: ChatMessage[];
  /** Read-receipt: ТОЧНЫЕ id прочитанных реплик посетителя. Свои уже
   *  отрисованные пузыри (их нет в messages — они до курсора after) виджет по
   *  этому списку поднимает до ✓✓. Список, а не максимум: лента кросс-канальная,
   *  прочитанные id не обязаны быть непрерывным префиксом. */
  seen_ids?: number[];
  /** Этот посетитель заблокирован (spec §12) — виджет исчезает молча на
   *  ближайшем поллинге, ленту дальше не разбираем. Поле НЕОБЯЗАТЕЛЬНОЕ и
   *  только true (старый сервер его не шлёт). */
  blocked?: boolean;
  /** Контакты для формы заказа сайта (Orders::Cart): что гость уже назвал в
   *  чате, виджет подставляет в ПУСТЫЕ поля оформления. Приходит только на
   *  первой выборке и только у магазина с описанной корзиной. */
  contact?: ContactInfo;
}

export interface PostMessageResponse {
  conversation_id: number;
  message: ChatMessage;
}

/** Тело POST /leads из формы виджета. Поля `comment`/`company` эндпоинт всё
 *  ещё принимает (их шлют формы на сайтах клиентов прямым POST), но САМА форма
 *  виджета их не спрашивает: разговор уже идёт, и второе поле «расскажите, что
 *  нужно» только удлиняет анкету. */
export interface LeadPayload {
  name: string;
  phone: string;
  email: string;
  visitor_key: string;
  page_path: string;
  /** ClientID счётчика хозяина страницы — ключ будущей оффлайн-конверсии по
   *  заявке. Пусто, когда Метрики на странице нет: сервер просто не отправит
   *  конверсию, всё остальное работает как прежде. */
  metrika_client_id?: string;
  /** Номер того же счётчика: сервер подставит его сотруднику, если поле пустое. */
  metrika_counter_id?: number;
}

/** Поле формы САЙТА, которое посетитель уже заполнил. Схему объявляет страница,
 *  платформа её не интерпретирует: подпись — то, что видит человек, значение —
 *  что он ввёл. Никаких доменных имён полей в коде виджета быть не должно. */
export interface FormField {
  key?: string;
  label?: string;
  value: string | number;
  unit?: string;
}

/** Как страница отдаёт поля: списком объектов или плоским «подпись → значение».
 *  Обе формы документированы в doc/site_chat_prefill.md и обе понимает сервер. */
export type FormFields = FormField[] | Record<string, string | number>;

/** Подсказка бота для формы сайта: «ключ поля → значение». Ключи — только те,
 *  что страница объявила сама (сервер сверяет по своей копии схемы). Заполняет
 *  форму САЙТ, «Отправить» жмёт человек: в чужой DOM виджет не лезет. */
export type FormPatch = Record<string, string>;

/** Контекст посетителя для «человечных» ответов: источник, таймзона, язык.
 *  device (моб/комп) — оператору в кабинете. */
export interface VisitorContext {
  referrer: string;
  timezone: string;
  locale: string;
  device: 'mobile' | 'desktop';
  /** ClientID Метрики: приходит с каждой репликой, сервер запоминает первый
   *  (Metrika::Visitor) — по нему заявка из РАЗГОВОРА получит свой ключ. */
  metrika_client_id?: string;
  /** Номер счётчика той же страницы (Metrika::Counter): избавляет клиента от
   *  ручного поиска номера в кабинете. 0 — Метрики на странице нет. */
  metrika_counter_id?: number;
  /** Посетитель пришёл по платному клику (`yclid` на посадочной). У магазина
   *  от этого зависит цена товара, и бот обязан называть ту же, что видит
   *  человек на карточке рядом. Шлём ОДИН флаг, а не адрес страницы: в чужой
   *  query-строке бывает что угодно, включая персональные данные. */
  ad_click?: boolean;
}

interface ErrorPayload {
  error?: string;
  message?: string;
  owner_notice?: string;
}

/** Не-2xx ответ API: статус + разобранное тело (нудж-тексты 402, 429 и т.д.). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly payload: ErrorPayload,
  ) {
    super(payload.message || `HTTP ${status}`);
    this.name = 'ApiError';
  }
}

/** Умения ЭТОГО бандла через запятую: сервер не предлагает боту то, чего
 *  кешированный на чужом сайте widget.js не исполнит. Строка, а не массив, —
 *  чтобы одинаково уехать и в JSON, и в multipart (там составные значения
 *  сериализуются иначе). */
const SUPPORTS = 'switch';

export class WidgetApi {
  /** @param base `${origin}/widget/${publicKey}` */
  constructor(private base: string) {}

  /** Origin платформы — оттуда догружаются адаптеры движков магазина
   *  (/widget-adapters/<name>.js): тот же хост, что раздал сам бандл. */
  get origin(): string {
    return new URL(this.base, window.location.href).origin;
  }

  /** Сменить адресата: коллега, которого позвали в разговор (блок 'switch').
   *  Базу правим на месте — виджет держит ОДИН экземпляр клиента, и менять его
   *  ссылку во всех обработчиках дороже, чем один адрес здесь. */
  switchKey(publicKey: string): void {
    this.base = this.base.replace(/\/widget\/[^/]+$/, `/widget/${encodeURIComponent(publicKey)}`);
  }

  /** @param visitorKey нужен серверу, чтобы сообщить состояние ИМЕННО этого
   *    посетителя (blocked): по чужому ключу оно недостижимо. Старый сервер
   *    лишний параметр игнорирует — конфиг тот же. */
  fetchConfig(visitorKey: string): Promise<WidgetConfig> {
    const query = new URLSearchParams({ visitor_key: visitorKey });
    // Язык страницы сайта — сигнал для языка интерфейса (см. language.ts).
    const lang = pageLanguage(document);
    if (lang) query.set('page_lang', lang);
    return this.request<WidgetConfig>('GET', `/config?${query.toString()}`);
  }

  /** @param seenThrough обратный read-receipt: id последней ПРОЧИТАННОЙ гостем
   *    реплики бизнеса (см. receipt.ts). 0 — отмечать нечего, параметр не шлём:
   *    отдельного публичного эндпоинта под отметку нет намеренно (spec §12). */
  /** @param checkout виджет стоит на странице оформления заказа сайта — сервер
   *    приложит контакты из разговора для формы (только на первой выборке). */
  fetchMessages(
    visitorKey: string,
    afterId: number,
    seenThrough = 0,
    checkout = false,
  ): Promise<MessagesResponse> {
    const query = new URLSearchParams({ visitor_key: visitorKey, after: String(afterId) });
    if (seenThrough > 0) query.set('seen_through', String(seenThrough));
    if (checkout) query.set('checkout', '1');
    const lang = pageLanguage(document);
    if (lang) query.set('page_lang', lang);
    return this.request<MessagesResponse>('GET', `/messages?${query.toString()}`);
  }

  /** Реплика посетителя; с вложениями уходит multipart/form-data (files[]),
   *  без них — прежний JSON. */
  postMessage(
    visitorKey: string,
    content: string,
    pagePath: string,
    context: VisitorContext,
    files: File[] = [],
    formContext: FormFields = [],
  ): Promise<PostMessageResponse> {
    const payload = {
      visitor_key: visitorKey,
      content,
      page_path: pagePath,
      // Что умеет ЭТОТ бандл: widget.js кешируется браузерами на чужих сайтах,
      // и сервер не должен предлагать боту то, чего клиент не исполнит
      // (передача разговора коллеге — блок 'switch').
      supports: SUPPORTS,
      // Что посетитель ввёл в форму САЙТА («Помощник формы»): сервер режет по
      // потолкам, маскирует ПД и кладёт в промпт рамкой «это данные».
      form_context: formContext,
      referrer: context.referrer,
      timezone: context.timezone,
      locale: context.locale,
      device: context.device,
      metrika_client_id: context.metrika_client_id ?? '',
      metrika_counter_id: context.metrika_counter_id ?? 0,
      ad_click: context.ad_click ? '1' : '',
    };
    if (files.length === 0) return this.request<PostMessageResponse>('POST', '/messages', payload);

    const body = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      // Поля формы — единственное составное значение в теле: multipart строк не
      // знает про массивы объектов, поэтому кладём их JSON-строкой (сервер
      // разбирает её тем же FormContext, что и обычный JSON-путь).
      if (Array.isArray(value)) return void (value.length > 0 && body.append(key, JSON.stringify(value)));
      body.append(key, value as string);
    });
    files.forEach((file) => body.append('files[]', file));
    return this.request<PostMessageResponse>('POST', '/messages', body);
  }

  postLead(payload: LeadPayload): Promise<unknown> {
    return this.request('POST', '/leads', payload);
  }

  /** Web Push подписка браузера (PushSubscription.toJSON): ответ ассистента
   *  догоняет посетителя после закрытия вкладки (см. push.ts). */
  postPushSubscription(visitorKey: string, subscription: PushSubscriptionJSON): Promise<unknown> {
    return this.request('POST', '/push', { visitor_key: visitorKey, ...subscription });
  }

  /** Диплинк «продолжить в мессенджере»: токен мятится на сервере в момент
   *  клика (одноразовый, TTL 30 минут) — поэтому запрос, а не URL из конфига.
   *  Недоступный канал — 404 (ApiError). */
  fetchContinueLink(kind: string, visitorKey: string, clientId = ''): Promise<{ url: string }> {
    const query = new URLSearchParams({ kind, visitor_key: visitorKey });
    // ClientID счётчика хозяина страницы: в мессенджере браузера нет, поэтому
    // ключ Метрики уезжает вместе с рукопожатием — иначе заявка из бота
    // остаётся без атрибуции. Пустой (блокировщик, счётчика нет) не шлём.
    if (clientId) query.set('metrika_client_id', clientId);
    return this.request<{ url: string }>('GET', `/continue?${query.toString()}`);
  }

  /** Presence heartbeat (виджет-паритет, волна 2): «посетитель на странице».
   *  Никаких PII/текста — только visitor_key (§9); сервер пишет штамп времени
   *  на существующем треде. Ответ (204) не парсим. */
  postPresence(visitorKey: string): Promise<Response> {
    return fetch(`${this.base}/presence`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitor_key: visitorKey }),
      keepalive: true,
    });
  }

  /** Финальный presence-beacon на уходе со страницы: sendBeacon переживает
   *  unload, где fetch отменяется (multipart/form-data — CORS-safelisted, без
   *  preflight). Возвращает false, если браузер отказался поставить beacon в
   *  очередь. Только visitor_key — PII в модель не уходит (§9). */
  presenceBeacon(visitorKey: string): boolean {
    if (typeof navigator === 'undefined' || !('sendBeacon' in navigator)) return false;

    const body = new FormData();
    body.append('visitor_key', visitorKey);
    return navigator.sendBeacon(`${this.base}/presence`, body);
  }

  /** FormData уходит как есть (браузер сам ставит multipart boundary),
   *  остальное — JSON. */
  private async request<T>(method: string, path: string, body?: object): Promise<T> {
    const multipart = body instanceof FormData;
    const response = await fetch(this.base + path, {
      method,
      headers: body && !multipart ? { 'Content-Type': 'application/json' } : undefined,
      body: multipart ? body : body && JSON.stringify(body),
    });
    const data: unknown = await response.json().catch(() => ({}));
    if (!response.ok) throw new ApiError(response.status, data as ErrorPayload);
    return data as T;
  }
}
